import { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HandymanIcon from '@mui/icons-material/Handyman';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import BadgeIcon from '@mui/icons-material/Badge';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PercentIcon from '@mui/icons-material/Percent';
import BuildIcon from '@mui/icons-material/Build';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import CampaignIcon from '@mui/icons-material/Campaign';
import FolderIcon from '@mui/icons-material/Folder';

import BreakdownBlock, { formatNumber, formatDate } from '../breakdownBlock/breakdownBlock';

// Icon per income section (fixed keys coming from the API)
const INCOME_ICONS = {
  card_sales: <CreditCardIcon />,
  cash_sales: <PaymentsIcon />,
  slip_first_installment: <ReceiptLongIcon />,
  slip_installments: <EventRepeatIcon />,
  technical_hours: <ScheduleIcon />,
  service: <HandymanIcon />,
  technical_support: <SupportAgentIcon />,
  accessories: <DevicesOtherIcon />,
  maintenance_fees: <AutorenewIcon />,
  other: <MoreHorizIcon />,
};

// Icon per cost group — matched on the description registered in Gerente
const EXPENSE_ICON_RULES = [
  [/SAL[ÁA]RIO/i, <BadgeIcon />],
  [/BENEF[ÍI]CIO/i, <CardGiftcardIcon />],
  [/B[ÔO]NUS|PREMIA/i, <EmojiEventsIcon />],
  [/AGREGADO/i, <GroupsIcon />],
  [/PESSOAL/i, <GroupsIcon />],
  [/PR[ÓO]-?\s?LABORE/i, <AccountBalanceWalletIcon />],
  [/IMPOSTO/i, <AccountBalanceIcon />],
  [/TARIFA/i, <PercentIcon />],
  [/MANUTEN/i, <BuildIcon />],
  [/MENSA(L|IS)/i, <EventRepeatIcon />],
  [/EXTRA/i, <ShoppingCartIcon />],
  [/PRODUTO|VENDA/i, <Inventory2Icon />],
  [/MARKETING/i, <CampaignIcon />],
];

const expenseIcon = (description) => {
  const rule = EXPENSE_ICON_RULES.find(([re]) => re.test(description || ''));
  return rule ? rule[1] : <FolderIcon />;
};

// Sections that are settled in one go: there is nothing to parcel out, so the entry
// is always the one and only installment
const SINGLE_INSTALLMENT_SECTIONS = new Set(['cash_sales', 'maintenance_fees']);

// Which installment this entry is paying, out of how many. The sale carries the
// installment count; everything else defaults to a single one, so the column is
// never blank — a payment is always at least "1/1".
const installments = (item, sectionKey) => {
  if (SINGLE_INSTALLMENT_SECTIONS.has(sectionKey)) return { number: 1, total: 1 };
  const number = item.installmentNumber > 0 ? item.installmentNumber : 1;
  const total = item.totalInstallments > 0 ? Math.max(item.totalInstallments, number) : number;
  return { number, total };
};

// onExpenseClick abre um lancamento pago para edicao. So as despesas recebem isso:
// uma receita e a baixa de um titulo do cliente, nao tem o que reclassificar aqui.
export default function IncomeExpenseBreakdown({ incomes, expenses, onExpenseClick }) {
  const theme = useTheme();
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';

  // Incomes: section (income type) -> client -> entries.
  // The API already hands the items over by client and, inside a client, in
  // installment sequence — a Map keeps that insertion order, so nothing is reordered
  // here (the block renders with childSort="source").
  const incomeCategories = useMemo(() => {
    const sections = incomes?.sections || {};
    return Object.entries(sections).map(([key, section]) => {
      const byClient = new Map();
      for (const item of section.items || []) {
        const name = item.clientName || 'Sem cliente';
        if (!byClient.has(name)) byClient.set(name, { name, value: 0, entries: [] });
        const client = byClient.get(name);
        client.value = +(client.value + (item.amountPaid || 0)).toFixed(2);
        const { number, total } = installments(item, key);
        client.entries.push({
          id: item.id,
          primary: item.paymentDate ? `Pago em ${formatDate(item.paymentDate)}` : 'Sem data de pagamento',
          secondary: [
            item.saleNumber ? `venda ${item.saleNumber}` : null,
            item.paymentMethod,
          ].filter(Boolean).join(' · '),
          badge: `${number}/${total}`,
          badgeTitle: `Parcela ${number} de ${total}`,
          value: item.amountPaid || 0,
        });
      }
      return {
        id: key,
        colorKey: key,
        name: section.title,
        icon: INCOME_ICONS[key] || <MoreHorizIcon />,
        value: section.subtotal || 0,
        children: Array.from(byClient.values()),
      };
    });
  }, [incomes]);

  // Expenses: cost group -> cost center -> entries
  const expenseCategories = useMemo(() => {
    const groups = expenses?.groups || [];
    return groups.map((group) => ({
      id: String(group.groupId ?? group.groupName),
      colorKey: String(group.groupId ?? group.groupName),
      name: group.groupName || 'Sem Grupo',
      icon: expenseIcon(group.groupName),
      value: group.subtotal || 0,
      children: (group.costCenters || []).map((center) => ({
        name: center.costCenterName,
        value: center.subtotal,
        // Cost centers and their entries come alphabetically from the API — the
        // ordering is part of the report, so it is not redone here
        entries: (center.entries || []).map((entry) => ({
          id: entry.id,
          primary: entry.supplier,
          secondary: [entry.description, entry.paymentDate ? formatDate(entry.paymentDate) : null]
            .filter(Boolean).join(' · '),
          value: entry.amountPaid || 0,
          // O que a tela de edicao precisa saber do lancamento. A entrada da lista
          // so carrega o que se le nela; o resto viaja aqui, intocado
          expense: {
            id: entry.id,
            description: entry.description,
            documentNumber: entry.documentNumber,
            supplier: entry.supplier,
            paymentDate: entry.paymentDate,
            amountPaid: entry.amountPaid || 0,
            costCenterId: center.costCenterId,
          },
        })),
      })),
    }));
  }, [expenses]);

  const totalIncomes = incomes?.grandTotal || 0;
  const totalExpenses = expenses?.grandTotal || 0;
  const balance = +(totalIncomes - totalExpenses).toFixed(2);
  const balanceColor = balance >= 0
    ? (mode === 'dark' ? '#199e70' : '#1baf7a')
    : (mode === 'dark' ? '#e66767' : '#e34948');

  return (
    <Box>
      {/* Period summary */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', mb: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Receitas</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{formatNumber(totalIncomes)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Despesas</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{formatNumber(totalExpenses)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Saldo</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: balanceColor }}>{formatNumber(balance)}</Typography>
        </Box>
      </Box>

      {/* Nothing folds into "Outros" here: every income section and every cost group
          is a line of its own, so the reading matches the closing sheet. In the
          incomes "Outros" is a section of the API, never an aggregate of the chart. */}
      <BreakdownBlock
        title="Receitas"
        categories={incomeCategories}
        mode={mode}
        maxSlices={Infinity}
        childSort="source"
        emptyLabel="Sem receitas no período"
      />

      <Box sx={{ height: 24 }} />

      <BreakdownBlock
        title="Despesas"
        categories={expenseCategories}
        mode={mode}
        maxSlices={Infinity}
        childSort="source"
        emptyLabel="Sem despesas no período"
        onEntryClick={onExpenseClick ? (entry) => onExpenseClick(entry.expense) : undefined}
      />
    </Box>
  );
}
